import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as React from "react";
import { FC, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import ReactTable, { RowInfo } from "react-table";
import * as ReactTooltip from "react-tooltip";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import { FormFilterInput } from "src/components/form";
import { Link } from "src/components/routing";
import { FontAwesomeIcons } from "src/constants/fontAwesomeIcons";
import { PAGE_SIZE_OPTIONS } from "src/constants/reactTableConstants";
import { InstitutionProceduresBreadcrumbs } from "src/helpers/breadcrumbHelper";
import {
    convertToApproximateDateString,
    convertToShortDateString,
} from "src/helpers/date";
import useNavigate from "src/hooks/useNavigate";
import * as InstitutionActions from "../../../actions/institution";
import * as InstitutionProcedureActions from "../../../actions/institutionProcedure";
import * as NavigationActions from "../../../actions/navigation";
import * as PagingActions from "../../../actions/pages/pagingPage";
import { RestrictedLayout } from "../../../components/layouts/index";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { InstitutionSubMenu } from "../../../helpers/subMenuHelper";
import { IFormFilter, ITmdState } from "../../../reducers/index";

import "./InstitutionProceduresPage.scss";

interface InstitutionProceduresPageProps {
    institution: Dtos.Institution;
    institutionProcedures: Dtos.InstitutionProcedure[];
    loadingInstitution: boolean;
    loadInstitutionSuccess: boolean;
    loadInstitutionFailure: boolean;
    loadInstitutionErrorCode: string;
    loadingInstitutionProcedures: boolean;
    loadInstitutionProceduresSuccess: boolean;
    loadInstitutionProceduresFailure: boolean;
    loadInstitutionProceduresErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    pageSize: number;
    formFilter: IFormFilter;
    clearInstitution: InstitutionActions.IInstitutionClearActionCreator;
    loadInstitution: InstitutionActions.IInstitutionLoadByIdActionCreator;
    clearInstitutionProcedure: InstitutionProcedureActions.IInstitutionProcedureClearActionCreator;
    loadInstitutionProcedure: InstitutionProcedureActions.IInstitutionProceduresLoadByInstitutionIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
    setPageSize: PagingActions.IPagingPageSizeChangedStateActionCreator;
    filter: InstitutionProcedureActions.IInstitutionProcedureFilterActionCreator;
}

const InstitutionProceduresPage: FC<InstitutionProceduresPageProps> = ({
    institution,
    institutionProcedures,
    loadingInstitution,
    loadInstitutionSuccess,
    loadInstitutionFailure,
    loadInstitutionErrorCode,
    loadingInstitutionProcedures,
    loadInstitutionProceduresSuccess,
    loadInstitutionProceduresFailure,
    loadInstitutionProceduresErrorCode,
    permissions,
    pageSize,
    formFilter,
    clearInstitution,
    loadInstitution,
    clearInstitutionProcedure,
    loadInstitutionProcedure,
    setBreadcrumbs,
    setDrawerItems,
    setPageSize,
    filter
}) => {
    //
    // -- Params --
    const { institutionId } = useParams<{ institutionId: string }>();
    const institutionIdNumber = parseInt(institutionId, 10);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showInstitutionProcedures =
        !loadingInstitution &&
        loadInstitutionSuccess &&
        !loadingInstitutionProcedures &&
        loadInstitutionProceduresSuccess;


    // Load Institution & Procedures
    useEffect(() => {
        if (institutionIdNumber && !institution) {
            setDrawerItems(
                InstitutionSubMenu(institutionIdNumber, permissions)
            );
            setBreadcrumbs(
                InstitutionProceduresBreadcrumbs(institutionIdNumber)
            );

            loadInstitution(institutionIdNumber);
            loadInstitutionProcedure(institutionIdNumber);
        }
    }, [
        institutionIdNumber,
        institution,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
        loadInstitution,
        loadInstitutionProcedure,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadInstitutionFailure) {
            navigate(`/error/${loadInstitutionErrorCode}`);
        }
        if (loadInstitutionProceduresFailure) {
            navigate(`/error/${loadInstitutionProceduresErrorCode}`);
        }
    }, [
        loadInstitutionFailure,
        loadInstitutionProceduresFailure,
        loadInstitutionErrorCode,
        loadInstitutionProceduresErrorCode,
        navigate,
    ]);

    // Update breadcrumbs
    useEffect(() => {
        if (institutionIdNumber && institution?.name) {
            setBreadcrumbs(
                InstitutionProceduresBreadcrumbs(
                    institutionIdNumber,
                    institution.name
                )
            );
        }
    }, [
        institution,
        institutionIdNumber,
        permissions,
        setDrawerItems,
        setBreadcrumbs,
    ]);

    // Cleanup effect to clear the institution & procedures on component unmount
    useEffect(() => {
        return () => {
            clearInstitution();
            clearInstitutionProcedure();
        };
    }, [clearInstitution, clearInstitutionProcedure]);

    const createTable = useMemo(() => {
        if (!institutionProcedures || institutionProcedures?.length === 0) {
            return (
                <div className="card card-shadow p-3">
                    <div className="alert alert-info d-flex mb-0">
                        <div className="mr-2">
                            <FontAwesomeIcon
                                icon={FontAwesomeIcons.Solid.INFO_SQUARE}
                                fixedWidth
                            />
                        </div>
                        <span>
                            There are currently no institution procedures to
                            view.
                        </span>
                    </div>
                </div>
            );
        }

        const showPagination = institutionProcedures.length > pageSize;

        return (
            <div className="table-wrapper">
                <ReactTable
                    data={institutionProcedures}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    defaultPageSize={
                        showPagination ? pageSize : institutionProcedures.length
                    }
                    pageSize={
                        showPagination ? pageSize : institutionProcedures.length
                    }
                    showPagination={showPagination}
                    className="-striped -highlight -clickable"
                    noDataText="There are currently no institution procedures to display"
                    resizable={false}
                    onPageSizeChange={(val) => setPageSize(val)}
                    getTdProps={(
                        _state,
                        rowInfo: RowInfo,
                        _column,
                        _instance
                    ) => {
                        return {
                            onClick: (_e, handleOriginal) => {
                                const url: string = `/institutions/${institutionIdNumber}/procedures/${rowInfo.original.id}/details`;
                                navigate(url);

                                if (handleOriginal) {
                                    handleOriginal();
                                }
                            },
                        };
                    }}
                    defaultSorted={[
                        {
                            id: "name",
                            desc: false,
                        },
                    ]}
                    columns={[
                        {
                            id: "id",
                            Header: " ",
                            accessor: "id",
                            maxWidth: 50,
                            Cell: (_props) => (
                                <FontAwesomeIcon
                                    size="lg"
                                    icon={FontAwesomeIcons.Duotone.BOOK}
                                    fixedWidth
                                />
                            ),
                        },
                        {
                            id: "name",
                            Header: "Name",
                            accessor: "name",
                        },
                        {
                            id: "dateOfProcedure",
                            Header: "Date of Procedure",
                            accessor: "dateOfProcedure",
                            maxWidth: 250,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                        {
                            id: "expiryDate",
                            Header: "Expiry Date",
                            accessor: "expiryDate",
                            maxWidth: 250,
                            Cell: (props) => (
                                <div>
                                    <span
                                        data-tip={convertToApproximateDateString(
                                            props.value
                                        )}
                                    >
                                        {convertToShortDateString(props.value)}
                                    </span>
                                    <ReactTooltip />
                                </div>
                            ),
                        },
                    ]}
                />
            </div>
        );
    }, [
        institution,
        institutionProcedures,
        pageSize,
        PAGE_SIZE_OPTIONS,
        navigate,
        setPageSize,
    ]);

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-auto mb-2"}>
                        <h1>Procedures</h1>
                    </div>
                    <div
                        className="col d-flex justify-content-end align-items-stretch"
                        style={{ height: "38px" }}
                    >
                        <FormFilterInput
                            formFilter={formFilter}
                            onFilter={(e) => filter(e)}
                        />
                        {AuthenticationHelper.hasPermission(
                            permissions,
                            Dtos.Permission.InstitutionProcedureCreate
                        ) && (
                            <Link
                                className={"btn btn-primary"}
                                url={`/institutions/${institutionIdNumber}/procedures/create`}
                            >
                                Create
                            </Link>
                        )}
                    </div>
                </div>
                {!showInstitutionProcedures ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showInstitutionProcedures ? (
                    <div className="card">
                        <div className="patient-tables">{createTable}</div>
                    </div>
                ) : null}
            </div>
        );
    }, [
        AuthenticationHelper,
        permissions,
        showInstitutionProcedures,
        createTable,
        formFilter,
        filter,
    ]);

    return (
        <RestrictedLayout
            permission={Dtos.Permission.InstitutionProcedureView}
            loading={loadingInstitution || loadingInstitutionProcedures}
        >
            {renderContent}
        </RestrictedLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<InstitutionProceduresPageProps> => {
    let institution: Dtos.Institution | undefined = !(
        state.institutions.formData instanceof Array
    )
        ? state.institutions.formData
        : undefined;
    let institutionProcedures: Dtos.InstitutionProcedure[] | undefined =
        state.institutionProcedure.formFilteredData instanceof Array
            ? state.institutionProcedure.formFilteredData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        institution: institution!,
        institutionProcedures: institutionProcedures!,
        loadingInstitution:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Pending,
        loadInstitutionSuccess:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Success,
        loadInstitutionFailure:
            state.institutions.loadState &&
            state.institutions.loadState.status == RequestState.Failure,
        loadInstitutionErrorCode: state.institutions.loadState.errorCode,
        loadingInstitutionProcedures:
            state.institutionProcedure.loadState &&
            state.institutionProcedure.loadState.status == RequestState.Pending,
        loadInstitutionProceduresSuccess:
            state.institutionProcedure.loadState &&
            state.institutionProcedure.loadState.status == RequestState.Success,
        loadInstitutionProceduresFailure:
            state.institutionProcedure.loadState &&
            state.institutionProcedure.loadState.status == RequestState.Failure,
        loadInstitutionProceduresErrorCode:
            state.institutionProcedure.loadState.errorCode,
        permissions: permissions,
        pageSize: state.pagingPage.pageSize,
        formFilter: state.institutionProcedure.formFilter,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<InstitutionProceduresPageProps> => {
    return {
        loadInstitution: bindActionCreators(
            InstitutionActions.LoadInstitutionById,
            dispatch
        ),
        clearInstitution: bindActionCreators(
            InstitutionActions.Clear,
            dispatch
        ),
        loadInstitutionProcedure: bindActionCreators(
            InstitutionProcedureActions.LoadInstitutionProceduresByInstitutionId,
            dispatch
        ),
        clearInstitutionProcedure: bindActionCreators(
            InstitutionProcedureActions.Clear,
            dispatch
        ),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        ),
        setPageSize: bindActionCreators(
            PagingActions.PageSizeChanged,
            dispatch
        ),
        filter: bindActionCreators(
            InstitutionProcedureActions.Filter,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(InstitutionProceduresPage);
